/**
 * InstallPWA — Round 122
 *
 * Shows two flavours of install prompts:
 *
 *  • Android / Chromium: listens for the `beforeinstallprompt` event
 *    and renders a floating "Install app" card. Tapping it calls
 *    the deferred prompt.prompt() → the browser's native install
 *    sheet appears. Dismissed prompts are remembered for 7 days.
 *
 *  • iOS Safari: no beforeinstallprompt support, so we render a
 *    static "Add to Home Screen" hint the first time an iOS Safari
 *    user visits AND the app isn't already running standalone. The
 *    hint stays dismissable and remembers the dismissal for 7 days.
 *
 * Renders NOTHING when:
 *  • The app is already running installed (display-mode: standalone)
 *  • The user dismissed within the last 7 days
 *  • The user is on a route that already opens a full-screen dialog
 *    (public tracker / login) — controlled by the caller mounting.
 */
import React, { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X } from "lucide-react";

const DISMISS_KEY = "adams-pwa-install-dismissed";
const DISMISS_DAYS = 7;

function isStandalone() {
  try {
    if (window.matchMedia("(display-mode: standalone)").matches) return true;
    // iOS Safari legacy flag
    if (typeof window.navigator.standalone === "boolean" && window.navigator.standalone) return true;
  } catch { /* noop */ }
  return false;
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  const iPadOS = /Mac/.test(ua) && "ontouchend" in document;
  return /iPad|iPhone|iPod/.test(ua) || iPadOS;
}

function isIOSSafari() {
  if (!isIOS()) return false;
  const ua = window.navigator.userAgent || "";
  // Chrome on iOS = CriOS, Firefox = FxiOS. Real Safari lacks these.
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

function dismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 3600 * 1000;
  } catch {
    return false;
  }
}

function stampDismiss() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
}


export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (dismissedRecently()) return;

    // Android / Chromium
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // Reset on install so we don't linger.
    const onInstalled = () => {
      setVisible(false);
      setIosHint(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari — no BIP event, show static hint on first visit.
    if (isIOSSafari()) {
      // Delay a moment so the user sees the app first.
      const t = setTimeout(() => setIosHint(true), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBIP);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const doInstall = async () => {
    if (!deferred) return;
    setInstalling(true);
    try {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "dismissed") stampDismiss();
      setDeferred(null);
      setVisible(false);
    } catch {
      setVisible(false);
    } finally {
      setInstalling(false);
    }
  };

  const dismiss = () => {
    stampDismiss();
    setVisible(false);
    setIosHint(false);
  };

  // Android/Chromium prompt card
  if (visible && deferred) {
    return (
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] w-[min(92vw,420px)] rounded-2xl bg-slate-900 text-white shadow-2xl shadow-black/40 border border-white/10 p-4"
        data-testid="pwa-install-prompt"
        role="dialog"
        aria-label="Install Adams Ops Desktop"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center overflow-hidden shrink-0">
            <img src="/adams-icon-192.png" alt="Adams" className="h-10 w-10 object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-extrabold text-sm">
              Install Adams Ops Desktop
            </div>
            <div className="text-xs text-white/70 mt-0.5">
              Add the app to your home screen for one-tap access and a fullscreen experience.
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="text-white/60 hover:text-white shrink-0"
            data-testid="pwa-install-dismiss-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={dismiss}
            className="text-xs font-semibold px-3 py-1.5 rounded-full text-white/70 hover:text-white"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={doInstall}
            disabled={installing}
            data-testid="pwa-install-btn"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-500/30 disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            {installing ? "Installing…" : "Install"}
          </button>
        </div>
      </div>
    );
  }

  // iOS Safari static hint
  if (iosHint) {
    return (
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] w-[min(92vw,420px)] rounded-2xl bg-white text-slate-900 shadow-2xl shadow-black/20 border border-slate-200 p-4"
        data-testid="pwa-ios-hint"
        role="dialog"
        aria-label="Add Adams Ops Desktop to Home Screen"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
            <img src="/adams-icon-192.png" alt="Adams" className="h-10 w-10 object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-extrabold text-sm">
              Add to Home Screen
            </div>
            <div className="text-xs text-slate-600 mt-0.5 space-y-1">
              <div className="flex items-center gap-1.5">
                <span>1. Tap the</span>
                <Share className="h-3.5 w-3.5 text-sky-600" strokeWidth={2.5} />
                <span>Share icon in Safari</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span>2. Choose</span>
                <PlusSquare className="h-3.5 w-3.5" strokeWidth={2.5} />
                <b className="text-slate-900">Add to Home Screen</b>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="text-slate-400 hover:text-slate-700 shrink-0"
            data-testid="pwa-ios-dismiss-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
